/**
 * Product Configurator
 * ---------------------
 * Handles parent + child add-on product bundling on the product page.
 *
 * Architecture:
 * - The main/parent product is the primary item on the page.
 * - Option groups (dropdowns) let the customer pick add-on products.
 * - On "Add to Cart", the parent and all selected add-ons are added
 *   as separate line items linked by a shared _bundle_id property.
 * - Child items carry _bundle_child = "true" so the cart can hide them.
 * - The parent item carries human-readable properties showing selections.
 *
 * Cart integration:
 * - Child line items are hidden in both the cart page and cart drawer.
 * - Quantity changes on the parent propagate to all children.
 * - Removing the parent removes all children.
 */

$(document).ready(function () {
  'use strict';

  /* ───────────────────────── DOM refs ───────────────────────── */

  var $container = $('[data-product-configurator]');
  if (!$container.length) return;

  var $form            = $container.find('[data-configurator-form]');
  var $addToCartBtn    = $container.find('[data-configurator-add]');
  var $addToCartText   = $addToCartBtn.find('[data-add-to-cart-text]');
  var $loader          = $addToCartBtn.find('[data-loader]');
  var $errorWrapper    = $container.find('[data-configurator-error]');
  var $errorMessage    = $container.find('[data-configurator-error-msg]');
  var $totalDisplay    = $container.find('[data-configurator-total]');
  var $optionGroups    = $container.find('[data-option-group]');

  /* ────────────────────── Config from Liquid ─────────────────── */

  var parentVariantId  = $container.find('[name="parent-variant-id"]').val();
  var parentProductId  = $container.attr('data-parent-product-id');

  /* ────────────────────── Price calculation ──────────────────── */

  function recalculateTotal() {
    var basePrice = parseInt($container.attr('data-parent-price'), 10) || 0;
    var addOnTotal = 0;

    $optionGroups.each(function () {
      var $select = $(this).find('select');
      var $selected = $select.find('option:selected');
      var price = parseInt($selected.attr('data-addon-price'), 10) || 0;
      addOnTotal += price;

      // Update the per-group price display
      var $priceDisplay = $(this).find('[data-option-price]');
      if (price > 0) {
        $priceDisplay.html('+ ' + formatMoney(price)).show();
      } else {
        $priceDisplay.html('').hide();
      }
    });

    var total = basePrice + addOnTotal;
    $totalDisplay.html(formatMoney(total));

    // Update currency if multi-currency is active
    try {
      if ((window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency) {
        Currency.convertAll(window.shop_currency, $('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
      }
    } catch (e) { /* multi-currency not active */ }
  }

  /**
   * Safe money formatter – falls back gracefully if theme.Currency is missing.
   */
  function formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Recalculate on every dropdown change
  $optionGroups.find('select').on('change', recalculateTotal);

  // Initial calculation
  recalculateTotal();

  /* ────────────────────── Validation ─────────────────────────── */

  function validate() {
    var valid = true;
    var firstInvalid = null;

    $optionGroups.each(function () {
      var $group = $(this);
      var required = $group.attr('data-required') === 'true';
      var $select = $group.find('select');
      var value = $select.val();

      // Clear previous error state
      $group.removeClass('configurator-group--error');

      if (required && (!value || value === '')) {
        valid = false;
        $group.addClass('configurator-group--error');
        if (!firstInvalid) firstInvalid = $group;
      }
    });

    if (!valid && firstInvalid) {
      showError('Please make a selection for all required options.');
      $('html, body').animate({
        scrollTop: firstInvalid.offset().top - 100
      }, 400);
    }

    return valid;
  }

  /* ────────────────────── Error display ──────────────────────── */

  function showError(msg) {
    $errorMessage.text(msg);
    $errorWrapper.removeClass('hide').attr('aria-hidden', 'false');
  }

  function hideError() {
    $errorWrapper.addClass('hide').attr('aria-hidden', 'true');
  }

  /* ────────────────────── Loading state ──────────────────────── */

  function setLoading(loading) {
    if (loading) {
      $addToCartBtn.attr('aria-disabled', 'true').addClass('btn--loading');
      $addToCartText.addClass('hide');
      $loader.removeClass('hide');
    } else {
      $addToCartBtn.removeAttr('aria-disabled').removeClass('btn--loading');
      $addToCartText.removeClass('hide');
      $loader.addClass('hide');
    }
  }

  /* ──────────────── Gather selected add-ons ─────────────────── */

  function getSelectedAddons() {
    var addons = [];
    $optionGroups.each(function () {
      var $group = $(this);
      var $select = $group.find('select');
      var $selected = $select.find('option:selected');
      var variantId = $selected.attr('data-addon-variant-id');
      var groupLabel = $group.attr('data-group-label');
      var optionLabel = $selected.text().trim();

      if (variantId && variantId !== '') {
        addons.push({
          variantId: variantId,
          groupLabel: groupLabel,
          optionLabel: optionLabel
        });
      }
    });
    return addons;
  }

  /**
   * Build human-readable selection properties for the parent line item.
   * These are visible in the cart under the parent product.
   */
  function getSelectionProperties() {
    var props = {};
    $optionGroups.each(function () {
      var $group = $(this);
      var $select = $group.find('select');
      var $selected = $select.find('option:selected');
      var groupLabel = $group.attr('data-group-label');
      var optionLabel = $selected.text().replace(/\s*\([^)]*\)\s*$/, '').trim(); // strip price suffix

      if ($selected.val() && $selected.val() !== '') {
        props[groupLabel] = optionLabel;
      }
    });
    return props;
  }

  /* ───────────── Sequential cart add (parent then children) ──── */

  /**
   * Adds items to the cart one at a time using Shopify's /cart/add.js.
   * We use sequential adds because Shopify doesn't support atomic
   * multi-item adds with different line item properties per item.
   *
   * @param {Array} items - Array of {id, quantity, properties}
   * @param {Function} callback - Called when all items are added
   */
  function addItemsSequentially(items, callback) {
    if (items.length === 0) {
      callback();
      return;
    }

    var item = items.shift();
    $.ajax({
      url: (window.router || '') + '/cart/add.js',
      type: 'POST',
      dataType: 'json',
      data: item
    })
    .done(function () {
      addItemsSequentially(items, callback);
    })
    .fail(function (xhr) {
      var msg = 'Could not add item to cart.';
      try { msg = xhr.responseJSON.description || msg; } catch (e) {}
      showError(msg);
      setLoading(false);
    });
  }

  /* ──────────────────── Main add-to-cart flow ────────────────── */

  // Prevent native form submission — this is AJAX-only
  $form.on('submit', function (e) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  });

  // Handle add-to-cart via button click
  $addToCartBtn.on('click', function (e) {
    e.preventDefault();
    e.stopPropagation();
    hideError();

    if (!validate()) return;

    setLoading(true);

    var quantity = parseInt($container.find('[data-quantity-input]').val(), 10) || 1;
    var bundleId = parentProductId + '-' + Date.now();
    var addons = getSelectedAddons();
    var selectionProps = getSelectionProperties();

    // Build the list of items to add
    var items = [];

    // 1. Parent item – carries visible selection properties + hidden linking keys
    var parentItem = {
      id: parentVariantId,
      quantity: quantity
    };
    // Visible properties: show each selected option under parent in the cart
    for (var key in selectionProps) {
      if (selectionProps.hasOwnProperty(key)) {
        parentItem['properties[' + key + ']'] = selectionProps[key];
      }
    }
    // Hidden properties (prefixed with _ so Shopify/theme hides them)
    parentItem['properties[_bundle_id]'] = bundleId;
    parentItem['properties[_bundle_parent]'] = 'true';

    items.push(parentItem);

    // 2. Child / add-on items – hidden in cart, linked by _bundle_id
    addons.forEach(function (addon) {
      var childItem = {
        id: addon.variantId,
        quantity: quantity
      };
      childItem['properties[_bundle_id]'] = bundleId;
      childItem['properties[_bundle_child]'] = 'true';
      childItem['properties[_bundle_parent_product]'] = parentProductId;
      childItem['properties[_bundle_group]'] = addon.groupLabel;
      items.push(childItem);
    });

    // Add all items sequentially
    addItemsSequentially(items, function () {
      setLoading(false);

      // Update header cart count and dropdown
      if (typeof Shopify !== 'undefined' && Shopify.getCart) {
        Shopify.getCart(function (cart) {
          var visibleCount = (window.BundleCart && window.BundleCart.getVisibleCartCount) ? window.BundleCart.getVisibleCartCount(cart) : cart.item_count;
          $('[data-cart-count]').text(visibleCount);

          // Update total price in header if present
          if ($('.navUser-action-total-price .total-price').length) {
            $('.navUser-action-total-price .total-price').html(
              formatMoney(cart.total_price)
            );
          }

          // Update dropdown cart
          if (typeof doUpdateDropdownCart === 'function') {
            doUpdateDropdownCart(cart);
          } else if (typeof updateDropdownCart === 'function') {
            updateDropdownCart();
          }

          // Show success feedback
          showAddedFeedback();
        });
      } else {
        // Fallback: just show success
        showAddedFeedback();
      }
    });
  });

  /* ────────────────────── Success feedback ───────────────────── */

  function showAddedFeedback() {
    var originalText = $addToCartText.text();
    $addToCartBtn.addClass('btn--added');
    $addToCartText.text('Added to Cart!');

    setTimeout(function () {
      $addToCartBtn.removeClass('btn--added');
      $addToCartText.text(originalText);
    }, 2500);
  }

});
